import apiService from '@/services/api'

export interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  meta?: { page: number; limit: number; total: number; totalPages: number }
}

export type UserRole =
  | 'admin'
  | 'operations_manager'
  | 'inventory_manager'
  | 'clearing_officer'
  | 'finance_team'

export interface User {
  id: string
  name: string
  email: string
  role: UserRole
  avatar?: string | null
  createdAt: string
  updatedAt: string
}

export interface UsersListResponse {
  users: User[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

export async function getUsers(params?: {
  page?: number
  limit?: number
  search?: string
  role?: string
}): Promise<UsersListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.page != null) searchParams.set('page', String(params.page))
  if (params?.limit != null) searchParams.set('limit', String(params.limit))
  if (params?.search) searchParams.set('search', params.search)
  if (params?.role) searchParams.set('role', params.role)
  const query = searchParams.toString()
  const url = query ? `/users?${query}` : '/users'
  const response = await apiService.get<ApiResponse<UsersListResponse>>(url)
  if (!response.success) throw new Error((response as any).message || 'Failed to fetch users')
  return response.data
}

export async function getUserById(id: string): Promise<User> {
  const response = await apiService.get<ApiResponse<User>>(`/users/${id}`)
  if (!response.success) throw new Error((response as any).message || 'Failed to fetch user')
  return response.data
}

export async function getCurrentUser(): Promise<User> {
  const response = await apiService.get<ApiResponse<User>>('/users/me')
  if (!response.success) throw new Error((response as any).message || 'Failed to fetch current user')
  return response.data
}

export interface CreateUserPayload {
  name: string
  email: string
  password: string
  role?: UserRole
}

export async function createUser(payload: CreateUserPayload): Promise<User> {
  const response = await apiService.post<ApiResponse<User>>('/users', payload)
  if (!response.success) throw new Error((response as any).message || 'Failed to create user')
  return response.data
}

export interface UpdateUserPayload {
  name?: string
  email?: string
  password?: string
  role?: UserRole
  avatar?: string | null
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<User> {
  const body: Record<string, unknown> = {}
  if (payload.name !== undefined) body.name = payload.name
  if (payload.email !== undefined) body.email = payload.email
  if (payload.password !== undefined && payload.password !== '') body.password = payload.password
  if (payload.role !== undefined) body.role = payload.role
  if (payload.avatar !== undefined) body.avatar = payload.avatar
  const response = await apiService.put<ApiResponse<User>>(`/users/${id}`, body)
  if (!response.success) throw new Error((response as any).message || 'Failed to update user')
  return response.data
}

export async function deleteUser(id: string): Promise<void> {
  const response = await apiService.delete<ApiResponse<null>>(`/users/${id}`)
  if (!response.success) throw new Error((response as any).message || 'Failed to delete user')
}

/** Upload avatar image for a user. Returns updated user with avatar URL. */
export async function uploadUserAvatar(userId: string, file: File): Promise<User> {
  const response = await apiService.uploadFile<ApiResponse<User>>(`/users/${userId}/avatar`, file)
  if (!response.success) throw new Error((response as any).message || 'Failed to upload avatar')
  return response.data
}

/** Base URL for backend (no /api) so /uploads/... paths resolve correctly. */
const uploadsBaseUrl = (() => {
  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
  return apiUrl.replace(/\/api\/?$/, '')
})()

/** Resolve avatar to a full URL for <img src>. Returns null if no avatar. */
export function getAvatarUrl(avatar: string | null | undefined): string | null {
  if (!avatar || !avatar.trim()) return null
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) return avatar
  return `${uploadsBaseUrl}${avatar.startsWith('/') ? '' : '/'}${avatar}`
}

/** Human-readable role labels for UI */
export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  operations_manager: 'Operations Manager',
  inventory_manager: 'Inventory Manager',
  clearing_officer: 'Clearing Officer',
  finance_team: 'Finance Team',
}
